import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Autocomplete, TextField, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import DataGridTable from "../Components/DataGridTable";
import { useDialog } from "../Components/DialogProvider";
import { useAuth } from "../hooks/AuthProvider";
import {
  exportPeopleCounterReport,
  getLatestPeopleCounterStatistics,
  getManyPeopleCounterStatistics,
  getOnePeopleCounterStatistics,
  getPeopleCounterDeviceOptions,
  getPeopleCounterOrganizations,
  getPeopleCounterRealtimeConfig,
  setPeopleCounterLineVisibility,
} from "../api/peopleCounterApi";
import {
  buildPeopleCounterChartData,
  buildPeopleCounterSummary,
  buildRealtimePeopleCounterRows,
  mergePeopleCounterRealtimeRows,
  normalizePeopleCounterRows,
} from "../utils/peopleCounterStats";
import { PeopleCounterRealtimeClient } from "../services/peopleCounterRealtime";
import PeopleCounterLayout, { usePeopleCounterTranslations } from "./PeopleCounterLayout";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.INFO || error?.message || fallback;


const getLineKey = (row) => {
  const mac = String(row?.mac_address || row?.macAddress || "").trim().toUpperCase();
  const line = row?.line;
  if (!mac || line === undefined || line === null || line === "" || line === "-") return null;
  return `${mac}::${line}`;
};

const utcDayBoundary = (date, endOfDay = false) =>
  date ? `${date}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}Z` : null;

const getDownloadFilename = (contentDisposition, fallback) => {
  const value = String(contentDisposition || "");
  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  const quotedMatch = value.match(/filename="([^"]+)"/i);
  const plainMatch = value.match(/filename=([^;]+)/i);
  const raw = utf8Match?.[1] || quotedMatch?.[1] || plainMatch?.[1];
  if (!raw) return fallback;

  try {
    return decodeURIComponent(String(raw).trim().replace(/^['"]|['"]$/g, "")) || fallback;
  } catch (_error) {
    return String(raw).trim().replace(/^['"]|['"]$/g, "") || fallback;
  }
};

const getBlobErrorMessage = async (error, fallback) => {
  const data = error?.response?.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.INFO || fallback;
    } catch (_error) {
      return fallback;
    }
  }
  return getErrorMessage(error, fallback);
};

export default function PeopleCounterStatistics() {
  const { org, permissions } = useAuth();
  const canViewStatistics = Boolean(permissions?.view_people_counter_statistics);
  const canManageLines = Boolean(permissions?.edit_people_counter_devices || permissions?.delete_people_counter_devices);
  const { t, lang } = usePeopleCounterTranslations();
  const dialog = useDialog();
  const [organizations, setOrganizations] = useState([]);
  const [organizationsReady, setOrganizationsReady] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [mode, setMode] = useState("latest");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [hiddenLineKeys, setHiddenLineKeys] = useState(() => new Set());
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingStatistics, setLoadingStatistics] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [devicesScopeKey, setDevicesScopeKey] = useState(null);
  const [realtimeConfig, setRealtimeConfig] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState("idle");
  const deviceSequence = useRef(0);
  const statisticsSequence = useRef(0);
  const realtimeEventsRef = useRef([]);
  const realtimeNeedsResyncRef = useRef(false);

  const locale = lang === "ar" ? "ar-AE" : lang === "ur" ? "ur-PK" : "en-GB";
  const organizationScopeKey = selectedOrganization?.id
    ? String(selectedOrganization.id)
    : "all";
  const allOrganizationsOption = useMemo(
    () => ({ id: null, name: t.allOrganizations, isAll: true }),
    [t.allOrganizations]
  );
  const organizationOptions = useMemo(
    () => [allOrganizationsOption, ...organizations],
    [allOrganizationsOption, organizations]
  );
  const selectedOrganizationOption = selectedOrganization || allOrganizationsOption;
  const dateRangeError = useMemo(() => {
    if (!fromDate && !toDate) return "";
    if (!fromDate || !toDate) return t.dateRangeIncomplete;
    if (toDate < fromDate) return t.dateRangeInvalid;
    return "";
  }, [fromDate, t.dateRangeIncomplete, t.dateRangeInvalid, toDate]);
  const timeFilter = useMemo(() => {
    if (!fromDate || !toDate || dateRangeError) return null;
    return {
      fromCreatedAt: utcDayBoundary(fromDate, false),
      toCreatedAt: utcDayBoundary(toDate, true),
    };
  }, [dateRangeError, fromDate, toDate]);

  useEffect(() => {
    if (!canViewStatistics) return undefined;
    let active = true;
    setOrganizationsReady(false);
    getPeopleCounterOrganizations()
      .then((response) => {
        if (!active) return;
        const options = Array.isArray(response.data?.data) ? response.data.data : [];
        const currentOrganization = options.find((item) => Number(item.id) === Number(org?.id));
        setOrganizations(options);
        setSelectedOrganization(currentOrganization || null);
        setOrganizationsReady(true);
      })
      .catch((requestError) => {
        if (!active) return;
        setOrganizations([]);
        setSelectedOrganization(null);
        setOrganizationsReady(true);
        setError(getErrorMessage(requestError, t.loadOrganizationsFailed));
      });
    return () => { active = false; };
  }, [canViewStatistics, org?.id, t.loadOrganizationsFailed]);

  useEffect(() => {
    if (!canViewStatistics) return undefined;
    let active = true;
    setRealtimeStatus("connecting");
    getPeopleCounterRealtimeConfig()
      .then((response) => {
        if (!active) return;
        const config = response.data?.data || null;
        setRealtimeConfig(config);
        setRealtimeStatus(config?.enabled ? "idle" : "disabled");
      })
      .catch(() => {
        if (!active) return;
        setRealtimeConfig(null);
        setRealtimeStatus("error");
      });
    return () => { active = false; };
  }, [canViewStatistics]);

  const loadDevices = useCallback(async () => {
    if (!canViewStatistics || !organizationsReady) return;
    const sequence = ++deviceSequence.current;
    const requestedScopeKey = organizationScopeKey;
    setDevicesScopeKey(null);
    setLoadingDevices(true);
    setError("");
    try {
      const response = await getPeopleCounterDeviceOptions({
        ...(selectedOrganization?.id ? { organizationId: selectedOrganization.id } : {}),
      });
      if (sequence !== deviceSequence.current) return;
      const nextDevices = Array.isArray(response.data?.data) ? response.data.data : [];
      setDevices(nextDevices);
      setSelectedDevice((current) => {
        const matching = nextDevices.find(
          (device) => Number(device.people_counter_id) === Number(current?.people_counter_id)
        );
        return matching || nextDevices[0] || null;
      });
      setDevicesScopeKey(requestedScopeKey);
    } catch (requestError) {
      if (sequence !== deviceSequence.current) return;
      setDevices([]);
      setSelectedDevice(null);
      setError(getErrorMessage(requestError, t.loadDevicesFailed));
    } finally {
      if (sequence === deviceSequence.current) setLoadingDevices(false);
    }
  }, [canViewStatistics, organizationScopeKey, organizationsReady, selectedOrganization?.id, t.loadDevicesFailed]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const loadStatistics = useCallback(async (signal) => {
    if (!canViewStatistics || !organizationsReady) return;
    if (loadingDevices || devicesScopeKey !== organizationScopeKey) return;
    if (mode === "many" && dateRangeError) return;
    if (!devices.length) {
      setRows([]);
      setLastUpdated(new Date());
      return;
    }
    if (mode === "one" && !selectedDevice?.people_counter_id) {
      setRows([]);
      setLastUpdated(null);
      return;
    }

    const sequence = ++statisticsSequence.current;
    const requestStartedAt = Date.now();
    setLoadingStatistics(true);
    setError("");
    try {
      const payload = {
        ...(selectedOrganization?.id ? { organizationId: selectedOrganization.id } : {}),
        ...(mode === "many" && timeFilter ? { timeFilter } : {}),
      };
      const response = mode === "one"
        ? await getOnePeopleCounterStatistics(selectedDevice.people_counter_id, { signal })
        : mode === "many"
          ? await getManyPeopleCounterStatistics(payload, { signal })
          : await getLatestPeopleCounterStatistics(payload, { signal });

      if (sequence !== statisticsSequence.current) return;
      const responseRows = normalizePeopleCounterRows(response.data?.data);
      const realtimeRowsDuringRequest = realtimeEventsRef.current
        .filter((entry) => entry.receivedAt >= requestStartedAt)
        .flatMap((entry) => entry.rows);
      setRows(mergePeopleCounterRealtimeRows(
        responseRows,
        realtimeRowsDuringRequest,
        mode,
        timeFilter
      ));
      setLastUpdated(new Date());
    } catch (requestError) {
      if (sequence !== statisticsSequence.current || requestError?.code === "ERR_CANCELED") return;
      setRows([]);
      setError(getErrorMessage(requestError, t.loadStatisticsFailed));
    } finally {
      if (sequence === statisticsSequence.current) setLoadingStatistics(false);
    }
  }, [canViewStatistics, dateRangeError, devices, devicesScopeKey, loadingDevices, mode, organizationScopeKey, organizationsReady, selectedDevice?.people_counter_id, selectedOrganization?.id, t.loadStatisticsFailed, timeFilter]);

  useEffect(() => {
    const controller = new AbortController();
    loadStatistics(controller.signal);
    return () => controller.abort();
  }, [loadStatistics, refreshKey]);

  const realtimeMacAddresses = useMemo(() => {
    if (mode === "one") {
      return selectedDevice?.mac_address ? [selectedDevice.mac_address] : [];
    }
    return devices.map((device) => device?.mac_address).filter(Boolean);
  }, [devices, mode, selectedDevice?.mac_address]);

  const realtimeSubscriptionKey = useMemo(
    () => realtimeMacAddresses.map((value) => String(value).toUpperCase()).sort().join("|"),
    [realtimeMacAddresses]
  );

  useEffect(() => {
    realtimeEventsRef.current = [];
    realtimeNeedsResyncRef.current = false;
  }, [organizationScopeKey, mode, selectedDevice?.people_counter_id, fromDate, toDate]);

  useEffect(() => {
    const scopeReady = !loadingDevices && devicesScopeKey === organizationScopeKey;
    if (!canViewStatistics || !scopeReady || !realtimeConfig?.enabled || !realtimeConfig?.url || !realtimeMacAddresses.length) {
      if (realtimeConfig && !realtimeConfig.enabled) setRealtimeStatus("disabled");
      else if (realtimeConfig?.enabled && (!scopeReady || !realtimeMacAddresses.length)) setRealtimeStatus("idle");
      return undefined;
    }

    const client = new PeopleCounterRealtimeClient({
      url: realtimeConfig.url,
      onStatus: (status) => {
        setRealtimeStatus(status);
        if (status === "reconnecting") realtimeNeedsResyncRef.current = true;
        if (status === "connected" && realtimeNeedsResyncRef.current) {
          realtimeNeedsResyncRef.current = false;
          // Reconcile through the scoped REST endpoint so readings missed during
          // the network gap cannot leave the dashboard stale after reconnect.
          setRefreshKey((value) => value + 1);
        }
      },
      onError: () => setRealtimeStatus("error"),
      onUpdate: (payload) => {
        const receivedAt = new Date();
        const liveRows = buildRealtimePeopleCounterRows(payload, devices, receivedAt);
        if (!liveRows.length) return;

        realtimeEventsRef.current = [
          ...realtimeEventsRef.current.filter((entry) => receivedAt.getTime() - entry.receivedAt < 120000),
          { receivedAt: receivedAt.getTime(), rows: liveRows },
        ].slice(-250);

        setRows((currentRows) => mergePeopleCounterRealtimeRows(
          currentRows,
          liveRows,
          mode,
          timeFilter
        ));
        setLastUpdated(receivedAt);
      },
    });

    client.start(realtimeMacAddresses);
    return () => client.stop(false);
  }, [
    canViewStatistics,
    devices,
    devicesScopeKey,
    loadingDevices,
    mode,
    organizationScopeKey,
    realtimeConfig,
    realtimeMacAddresses,
    realtimeSubscriptionKey,
    timeFilter,
  ]);

  const realtimeStatusLabel = useMemo(() => ({
    connected: t.liveConnected,
    connecting: t.liveConnecting,
    reconnecting: t.liveReconnecting,
    error: t.liveDisconnected,
    disabled: t.liveDisabled,
    idle: realtimeConfig?.enabled ? t.liveWaiting : t.liveDisabled,
  }[realtimeStatus] || t.liveDisconnected), [realtimeConfig?.enabled, realtimeStatus, t]);

  const handleExportReport = useCallback(async () => {
    if (!timeFilter) {
      setError(t.dateRangeRequiredForExport);
      return;
    }
    if (!devices.length || loadingDevices || exporting) return;

    setExporting(true);
    setError("");
    try {
      const response = await exportPeopleCounterReport({
        ...(selectedOrganization?.id ? { organizationId: selectedOrganization.id } : {}),
        timeFilter,
      });
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: response.headers?.["content-type"] || "application/octet-stream" });
      const fallbackName = `people-counter-report-${fromDate}-to-${toDate}.xlsx`;
      const filename = getDownloadFilename(response.headers?.["content-disposition"], fallbackName);
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.style.display = "none";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch (requestError) {
      setError(await getBlobErrorMessage(requestError, t.exportFailed));
    } finally {
      setExporting(false);
    }
  }, [devices.length, exporting, fromDate, loadingDevices, selectedOrganization?.id, t.dateRangeRequiredForExport, t.exportFailed, timeFilter, toDate]);

  const dashboardRows = useMemo(() => {
    if (mode !== "latest" || !hiddenLineKeys.size) return rows;
    return rows.filter((row) => {
      const key = getLineKey(row);
      return !key || !hiddenLineKeys.has(key);
    });
  }, [hiddenLineKeys, mode, rows]);

  const hideLineFromDashboard = useCallback(async (row) => {
    if (!canManageLines || mode !== "latest") return;
    const line = Number(row?.line);
    const peopleCounterId = Number(row?.people_counter_id);
    if (!Number.isInteger(line) || line < 0 || !Number.isInteger(peopleCounterId) || peopleCounterId <= 0) return;

    const lineLabel = row?.line_name && row.line_name !== "-" ? row.line_name : `${t.line} ${line}`;
    const confirmed = await dialog.confirm({
      title: t.hideLineTitle,
      message: t.hideLineMessage.replace("{line}", lineLabel),
      confirmText: t.hideLine,
      cancelText: t.cancel,
    });
    if (!confirmed) return;

    try {
      await setPeopleCounterLineVisibility(peopleCounterId, { line, isHidden: true });
      const key = getLineKey(row);
      if (key) {
        setHiddenLineKeys((current) => {
          const next = new Set(current);
          next.add(key);
          return next;
        });
      }
      await dialog.alert({ variant: "success", title: t.hideLineTitle, message: t.hideLineSuccess });
      setRefreshKey((value) => value + 1);
    } catch (requestError) {
      await dialog.alert({
        variant: "error",
        title: t.hideLineTitle,
        message: getErrorMessage(requestError, t.hideLineFailed),
      });
    }
  }, [canManageLines, dialog, mode, t]);

  const summary = useMemo(
    () => buildPeopleCounterSummary(dashboardRows, devices.length),
    [dashboardRows, devices.length]
  );
  const chartData = useMemo(() => buildPeopleCounterChartData(dashboardRows), [dashboardRows]);
  const columns = useMemo(() => {
    const nextColumns = [];
    if (mode === "latest" && canManageLines) {
      nextColumns.push({
        field: "actions",
        headerName: t.actions,
        type: "actions",
        width: 90,
        getActions: (params) => {
          const line = Number(params.row?.line);
          const peopleCounterId = Number(params.row?.people_counter_id);
          if (!Number.isInteger(line) || line < 0 || !Number.isInteger(peopleCounterId) || peopleCounterId <= 0) return [];
          return [
            <GridActionsCellItem
              key="hide-line"
              icon={<Tooltip title={t.hideLine}><DeleteOutlineIcon color="error" /></Tooltip>}
              label={t.hideLine}
              onClick={() => hideLineFromDashboard(params.row)}
              showInMenu
            />,
          ];
        },
      });
    }

    nextColumns.push(
      { field: "people_counter_name", headerName: t.deviceName, minWidth: 180, flex: 1 },
      { field: "mac_address", headerName: t.macAddress, minWidth: 190, flex: 1 },
      { field: "organization_name", headerName: t.organization, minWidth: 170, flex: 1 },
      { field: "line", headerName: t.line, width: 90, align: "center", headerAlign: "center" },
      { field: "line_name", headerName: t.lineName, minWidth: 150, flex: 1 },
      { field: "traffic_entered", headerName: t.entered, width: 110, type: "number" },
      { field: "traffic_exited", headerName: t.exited, width: 110, type: "number" },
      { field: "children_in", headerName: t.childrenIn, width: 120, type: "number" },
      { field: "children_out", headerName: t.childrenOut, width: 125, type: "number" },
      { field: "group_in", headerName: t.groupIn, width: 110, type: "number" },
      { field: "group_out", headerName: t.groupOut, width: 115, type: "number" },
      { field: "staff_in", headerName: t.staffIn, width: 105, type: "number" },
      { field: "staff_out", headerName: t.staffOut, width: 110, type: "number" },
      {
        field: "window_start",
        headerName: t.windowStart,
        minWidth: 185,
        flex: 1,
        valueFormatter: (value) => value ? new Date(value).toLocaleString(locale) : "-",
      },
      {
        field: "window_end",
        headerName: t.windowEnd,
        minWidth: 185,
        flex: 1,
        valueFormatter: (value) => value ? new Date(value).toLocaleString(locale) : "-",
      },
      {
        field: "received_at",
        headerName: t.readingTime,
        minWidth: 190,
        flex: 1,
        type: "dateTime",
        valueGetter: (value) => {
          if (!value) return null;
          const date = new Date(value);
          return Number.isNaN(date.getTime()) ? null : date;
        },
        valueFormatter: (value) =>
          value instanceof Date && !Number.isNaN(value.getTime())
            ? value.toLocaleString(locale)
            : "-",
      }
    );
    return nextColumns;
  }, [canManageLines, hideLineFromDashboard, locale, mode, t]);

  const summaryCards = [
    { label: t.registeredDevices, value: summary.registeredDevices, icon: DevicesOtherOutlinedIcon },
    { label: t.reportingDevices, value: summary.reportingDevices, icon: SensorsOutlinedIcon },
    { label: t.currentOccupancy, value: summary.currentOccupancy, icon: GroupsOutlinedIcon },
  ];

  return (
    <PeopleCounterLayout title={t.statisticsTitle} description={t.statisticsDescription} requiredPermission="view_people_counter_statistics">
      <section className="people-counter-card people-counter-stats-controls">
        <Autocomplete
          className="people-counter-field"
          options={organizationOptions}
          value={selectedOrganizationOption}
          loading={!organizationsReady}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
          onChange={(_event, value) => setSelectedOrganization(value?.isAll ? null : value)}
          renderInput={(params) => <TextField {...params} size="small" label={t.filterOrganization} />}
        />
        <div className="people-counter-mode-control">
          <span className="people-counter-control-label">{t.dataMode}</span>
          <div className="people-counter-mode-switch" role="radiogroup" aria-label={t.dataMode}>
            {[
              ["latest", t.latestMode],
              ["many", t.historyMode],
              ["one", t.singleMode],
            ].map(([value, label]) => (
              <button
                type="button"
                role="radio"
                aria-checked={mode === value}
                className={mode === value ? "active" : ""}
                key={value}
                onClick={() => setMode(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {mode === "one" ? (
          <Autocomplete
            className="people-counter-field people-counter-device-filter"
            loading={loadingDevices}
            options={devices}
            value={selectedDevice}
            getOptionLabel={(option) => `${option?.name || ""} — ${option?.mac_address || ""}`}
            isOptionEqualToValue={(option, value) => Number(option.people_counter_id) === Number(value.people_counter_id)}
            onChange={(_event, value) => setSelectedDevice(value)}
            renderInput={(params) => <TextField {...params} size="small" label={t.device} />}
          />
        ) : null}
        <button
          type="button"
          className="devices-secondary-btn people-counter-button people-counter-refresh"
          onClick={() => setRefreshKey((value) => value + 1)}
          disabled={loadingStatistics || loadingDevices || !devices.length}
        >
          <RefreshIcon fontSize="small" /> <span>{t.refresh}</span>
        </button>
        <div className="people-counter-live-meta">
          <div
            className={`people-counter-live-status people-counter-live-status--${realtimeStatus}`}
            title={realtimeStatusLabel}
            aria-live="polite"
          >
            <span className="people-counter-live-dot" aria-hidden="true" />
            <span>{t.liveRealtime}: {realtimeStatusLabel}</span>
          </div>
          <span className="people-counter-last-updated">
            {lastUpdated ? `${t.lastUpdated}: ${lastUpdated.toLocaleString(locale)}` : t.notUpdatedYet}
          </span>
        </div>
        {mode === "many" ? (
          <div className="people-counter-date-range" aria-label={t.dateRange}>
            <TextField
              className="people-counter-field people-counter-date-field"
              size="small"
              type="date"
              label={t.fromDate}
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toDate || undefined }}
              error={Boolean(dateRangeError)}
            />
            <TextField
              className="people-counter-field people-counter-date-field"
              size="small"
              type="date"
              label={t.toDate}
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: fromDate || undefined }}
              error={Boolean(dateRangeError)}
            />
            <button
              type="button"
              className="devices-secondary-btn people-counter-button people-counter-date-clear"
              onClick={() => { setFromDate(""); setToDate(""); }}
              disabled={!fromDate && !toDate}
            >
              {t.clearDates}
            </button>
            <button
              type="button"
              className="devices-secondary-btn people-counter-button people-counter-export"
              onClick={handleExportReport}
              disabled={Boolean(dateRangeError) || !timeFilter || !devices.length || loadingDevices || exporting}
              title={timeFilter ? t.exportReport : t.dateRangeRequiredForExport}
            >
              <FileDownloadOutlinedIcon fontSize="small" />
              <span>{exporting ? t.exportingReport : t.exportReport}</span>
            </button>
            {dateRangeError ? <span className="people-counter-date-error">{dateRangeError}</span> : null}
          </div>
        ) : null}
      </section>

      {error ? <div className="people-counter-error">{error}</div> : null}

      <section className="people-counter-summary">
        {summaryCards.map(({ label, value, icon: Icon }) => (
          <article className="people-counter-card people-counter-summary-card" key={label}>
            <div className="people-counter-summary-icon"><Icon aria-hidden="true" /></div>
            <div className="people-counter-summary-content">
              <span>{label}</span>
              <strong>{Number(value || 0).toLocaleString(locale)}</strong>
            </div>
          </article>
        ))}
      </section>

      <section className="people-counter-visuals">
        <article className="people-counter-card people-counter-chart">
          <h2>{t.deviceTotalsChart}</h2>
          {chartData.length ? (
            <div className="people-counter-chart-canvas">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 12, right: 18, left: -8, bottom: 24 }} barCategoryGap="28%">
                  <CartesianGrid
                    stroke="var(--devices-grid-cell-border-strong)"
                    strokeDasharray="3 5"
                    vertical={false}
                  />
                <XAxis
                  dataKey="name"
                  angle={-16}
                  textAnchor="end"
                  height={54}
                  tick={{ fill: "var(--dg-toolbar-label)", fontSize: 11 }}
                  axisLine={{ stroke: "var(--devices-grid-cell-border-strong)" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "var(--dg-toolbar-label)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <ChartTooltip
                  cursor={false}
                  contentStyle={{
                    background: "var(--dashboard-modal-bg)",
                    border: "1px solid var(--devices-grid-cell-border-strong)",
                    borderRadius: 10,
                    color: "var(--cell-color)",
                  }}
                />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ paddingTop: 8, fontSize: 12 }} />
                <Bar dataKey="peopleIn" name={t.entered} fill="#16c8a8" radius={[5, 5, 0, 0]} maxBarSize={58} />
                  <Bar dataKey="peopleOut" name={t.exited} fill="#3f8cff" radius={[5, 5, 0, 0]} maxBarSize={58} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="people-counter-empty">{t.noStatistics}</div>}
        </article>

        <article className="people-counter-card people-counter-readings">
          <h2>{t.readingsTable}</h2>
          <div className="devices-grid-wrapper people-counter-readings-grid">
            <DataGridTable
              columns={columns}
              rows={dashboardRows}
              idcolumn="_row_id"
              pagingoptions={[10, 20, 50, 100]}
              sortField="received_at"
              sortType="desc"
              tableKey="people_counter_statistics"
              loading={loadingStatistics}
              showToolbar
            />
          </div>
        </article>
      </section>
    </PeopleCounterLayout>
  );
}
